<template>
  <v-row>
    <v-col cols="12" sm="12" md="4" lg="3">
      <v-sheet elevation="2" class="rounded pa-4 ma-o filter-sticky">
        <AuditLogsFilters
          :filtersEnabled="filtersEnabled"
          @filtersSet="onFiltersSet"
          @onClear="onClearFilters"
          @events="events => (eventOptions = events)"
          @targetTypes="targetTypes => (targetTypeOptions = targetTypes)"
        />
      </v-sheet>
    </v-col>
    <v-col cols="12" sm="12" md="8" lg="9">
      <EmptyPageStateManager
        v-if="logs.length === 0 || isLoading"
        :isLoading="isLoading"
        :title="$t('common.no_results_found')"
        :subtitle="$t('auditlogs.logs_not_found_subtitle')"
      />
      <template v-else>
        <v-card class="table-responsive overflow-x mt-0">
          <v-row class="pa-4 ma-0 flex-row-reverse">
            <ColumnsSelector
              :headers="headers"
              @toggleColumn="onToggleColumn"
            />
            <Button
              class="mr-4"
              color="blueGrey"
              icon="mdi-sync"
              @click="emitUpdateTableEvent"
              :text="$t('common.refresh')"
              plain
            />
          </v-row>
          <v-divider class="ma-0" />
          <v-card-text class="pa-4">
            <v-sheet outlined rounded>
              <v-data-table
                data-private
                :headers="headersToDisplay"
                :footer-props="footerOptions"
                :items="logs"
                :items-per-page="itemsPerPage"
                :loading="isLoading"
                :page="currentPage"
                :server-items-length="totalItems"
                :options="options"
                hide-default-footer
              >
                <template v-slot:item.eventTime="{ item }">
                  <span class="eventTime " @click="showRow(item._id)">
                    {{ formatDate(item.eventTime, 'MMMM D, YYYY, hh:mm a') }}
                  </span>
                </template>
                <template v-slot:item.eventName="{ item }">
                  <span class="eventName" @click="showRow(item._id)">
                    {{ getEventName(item.eventName) }}
                  </span>
                </template>
                <template v-slot:item.eventSource="{ item }">
                  <span class="eventSource" @click="showRow(item._id)">
                    {{ item.eventSource }}
                  </span>
                </template>
                <template v-slot:item.sourceType="{ item }">
                  <span class="sourceType" @click="showRow(item._id)">
                    {{ item.sourceType }}
                  </span>
                </template>
                <template v-slot:item.action="{ item }">
                  <span class="action">
                    {{ item.action }}
                  </span>
                </template>
                <template v-slot:item.eventDescription="{ item }">
                  <span class="eventDescription">
                    {{ formatEventDescription(item) }}
                  </span>
                </template>
                <template v-slot:item.sourceAccountName="{ item }">
                  <router-link
                    class="text-decoration-none"
                    :to="
                      `/profile/${item.sourceOrganizationId}/${item.sourceAccountId}`
                    "
                  >
                    {{ item.sourceAccountName }}
                  </router-link>
                </template>
                <template v-slot:item.sourceAccountId="{ item }">
                  <span class="sourceAccountId">
                    {{ item.sourceAccountId }}
                  </span>
                </template>
                <template v-slot:item.sourceOrganizationId="{ item }">
                  {{ item.sourceOrganizationId }}
                </template>
                <template v-slot:item.targetType="{ item }">
                  <span class="targetType">
                    {{ getTargetTypeName(item.targetType) }}
                  </span>
                </template>
                <template v-slot:item.targetId="{ item }">
                  <span
                    v-if="
                      item.targetType === 'rev' ||
                        item.targetType === 'challenge'
                    "
                  >
                    <router-link
                      clas="text-decoration-none"
                      :to="formatTargetIdLink(item)"
                      >{{ item.targetId }}</router-link
                    >
                  </span>
                  <span v-else>
                    {{ item.targetId }}
                  </span>
                </template>
                <template v-slot:item.targetAccountId="{ item }">
                  <router-link
                    class="text-decoration-none"
                    :to="
                      `/profile/${item.targetOrganizationId}/${item.targetAccountId}`
                    "
                  >
                    {{ item.targetAccountId }}
                  </router-link>
                </template>
                <template v-slot:item.targetOrganizationId="{ item }">
                  {{ item.targetOrganizationId }}
                </template>
                <template v-slot:item.admin__targetOrganizationId="{ item }">
                  <span>
                    {{ item.targetOrganizationId }}
                  </span>
                </template>
                <template v-slot:item.sourceAccountIP="{ item }">
                  {{ item.sourceAccountIP }}
                </template>
                <template v-slot:item.systemIP="{ item }">
                  <span>
                    {{ item.systemIP }}
                  </span>
                </template>
                <template v-slot:item.systemRegion="{ item }">
                  <span>
                    {{ item.systemRegion }}
                  </span>
                </template>
                <template v-slot:item.userAgent="{ item }">
                  <span>
                    {{ item.userAgent }}
                  </span>
                </template>
                <template v-slot:item.userAgentVersion="{ item }">
                  <span>
                    {{ item.userAgentVersion }}
                  </span>
                </template>

                <template v-slot:item.responseType="{ item }">
                  <span>
                    {{ item.responseType }}
                  </span>
                </template>
                <template v-slot:item.responseCode="{ item }">
                  <span>
                    {{ item.responseCode }}
                  </span>
                </template>
                <template v-slot:item.loggedAt="{ item }">
                  <span>
                    {{ formatDate(item.loggetAt, 'MMMM D, YYYY, hh:mm a') }}
                  </span>
                </template>
                <template v-slot:item.context="{ item }">
                  <a href="#" @click="openContextModal(item)">
                    {{ $t('common.context') }}
                  </a>
                </template>
              </v-data-table>
            </v-sheet>
            <v-container v-if="pages > 1 && logs.length > 0">
              <v-row justify="center" class="my-0">
                <v-col cols="8">
                  <v-container class="max-width">
                    <v-pagination
                      circle
                      class="my-4"
                      total-visible="10"
                      :length="pages"
                      :value="currentPage"
                      @input="handleChangeCurrentPage"
                    ></v-pagination>
                  </v-container>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-dialog
            scrollable
            max-width="650"
            v-model="modalIsOpen"
            @click:outside="closeModal"
            @keydown.esc="closeModal"
          >
            <v-card>
              <v-card-title>
                {{ $t('auditlogs.Context') }}
              </v-card-title>

              <v-card-text>
                <div class="text-left">
                  <pre>{{ context }}</pre>
                </div>
              </v-card-text>

              <v-card-actions class="justify-end">
                <Button
                  plain
                  class="me-2"
                  color="gray darken-1"
                  :text="$t('common.button.close')"
                  @click="closeModal"
                />
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-card>
      </template>
    </v-col>
  </v-row>
</template>

<script>
import mixin from '@/mixins/mixin.vue'

import EmptyPageStateManager from '@/components/utils/EmptyPageStateManager.vue'
import Button from '@/components/UI/Button'
import ColumnsSelector from '@/components/ColumnsSelector.vue'
import AuditLogsFilters from './AuditLogsFilters.vue'

import { mapState, mapActions, mapMutations } from 'vuex'
import _ from 'lodash'
import { utils } from 'frontend-js-shared'

const INITIAL_ITEMS_PER_PAGE = 20

/**
 * Possible values for columnsEnabled are the same as the
 * keys in defaultHeaders
 */
export default {
  name: 'LogsTable',

  components: {
    EmptyPageStateManager,
    Button,
    ColumnsSelector,
    AuditLogsFilters,
  },

  props: {
    columnsEnabled: {
      type: Array,
      required: true,
    },
    filtersEnabled: {
      type: Array,
    },
  },

  mixins: [mixin],

  data() {
    return {
      rowShown: null,
      context: {},
      modalIsOpen: false,
      defaultHeaders: {
        eventTime: {
          text: this.$i18n.t('auditlogs.event_time'),
          value: 'eventTime',
          sortable: false,
          show: true,
          width: 140,
        },
        eventName: {
          text: this.$i18n.t('auditlogs.event_name'),
          value: 'eventName',
          sortable: false,
          show: true,
        },
        eventSource: {
          text: this.$i18n.t('auditlogs.filters.event_source'),
          value: 'eventSource',
          sortable: false,
          show: true,
        },
        sourceType: {
          text: this.$i18n.t('auditlogs.filters.source_type'),
          value: 'sourceType',
          sortable: false,
          show: true,
        },
        action: {
          text: this.$i18n.t('common.action_one'),
          value: 'action',
          sortable: false,
          show: true,
        },
        eventDescription: {
          text: this.$i18n.t('auditlogs.event_description'),
          value: 'eventDescription',
          sortable: false,
          show: true,
        },
        sourceAccountName: {
          text: this.$i18n.t('auditlogs.source_account_name'),
          value: 'sourceAccountName',
          sortable: false,
          show: true,
          width: 150,
        },
        sourceAccountId: {
          text: this.$i18n.t('auditlogs.filters.source_account_id'),
          value: 'sourceAccountId',
          sortable: false,
          show: true,
        },
        sourceOrganizationId: {
          text: this.$i18n.t('auditlogs.filters.source_organization_id'),
          value: 'sourceOrganizationId',
          sortable: false,
          show: true,
        },
        targetType: {
          text: this.$i18n.t('auditlogs.filters.target_type'),
          value: 'targetType',
          sortable: false,
          show: true,
        },
        targetId: {
          text: this.$i18n.t('auditlogs.filters.target_id'),
          value: 'targetId',
          sortable: false,
          show: true,
        },
        targetAccountId: {
          text: this.$i18n.t('auditlogs.filters.target_account_id'),
          value: 'targetAccountId',
          sortable: false,
          show: true,
        },
        admin__targetOrganizationId: {
          text: this.$i18n.t('auditlogs.filters.target_organization_id'),
          value: 'admin__targetOrganizationId',
          sortable: false,
          show: true,
        },
        targetOrganizationId: {
          text: this.$i18n.t('auditlogs.filters.target_organization_id'),
          value: 'targetOrganizationId',
          sortable: false,
          show: true,
        },
        sourceAccountIP: {
          text: this.$i18n.t('auditlogs.source_account_ip'),
          value: 'sourceAccountIP',
          sortable: false,
          show: true,
        },
        systemIP: {
          text: this.$i18n.t('auditlogs.system_ip'),
          value: 'systemIP',
          sortable: false,
          show: true,
        },
        systemRegion: {
          text: this.$i18n.t('auditlogs.system_region'),
          value: 'systemRegion',
          sortable: false,
          show: true,
        },
        userAgent: {
          text: this.$i18n.t('auditlogs.user_agent'),
          value: 'userAgent',
          sortable: false,
          show: true,
          width: 150,
        },
        userAgentVersion: {
          text: this.$i18n.t('auditlogs.user_agent_version'),
          value: 'userAgentVersion',
          sortable: false,
          show: true,
        },
        responseType: {
          text: this.$i18n.t('auditlogs.filters.response_type'),
          value: 'responseType',
          sortable: false,
          show: true,
        },
        responseCode: {
          text: this.$i18n.t('auditlogs.filters.response_code'),
          value: 'responseCode',
          sortable: false,
          show: true,
        },
        loggedAt: {
          text: this.$i18n.t('auditlogs.logged_at'),
          value: 'loggedAt',
          sortable: false,
          show: true,
          width: 140,
        },
        context: {
          text: this.$i18n.t('auditlogs.context'),
          value: 'context',
          sortable: false,
          show: true,
        },
      },
      tableStatus: {
        sortBy: null,
        sortOrder: 'desc',
      },
      tableFilter: '',
      tableFilterKeys: [],
      footerOptions: {
        itemsPerPageOptions: [20, 50, 100],
      },
      currentPage: 1,
      itemsPerPage: INITIAL_ITEMS_PER_PAGE,
      eventOptions: [],
      targetTypeOptions: [],
    }
  },

  computed: {
    ...mapState({
      logs: state => state.audit.logs,
      pages: state => state.audit.totalPages,
      totalItems: state => state.audit.totalItems,
      isLoading: state => state.loader.showing,
    }),

    headers() {
      let headers = []
      let columnsEnabled = this.columnsEnabled
      let defaultHeaders = this.defaultHeaders

      Object.keys(defaultHeaders).forEach(key => {
        if (columnsEnabled.includes(key)) {
          headers.push(defaultHeaders[key])
        }
      })

      return headers
    },

    headersToDisplay() {
      return this.headers.filter(header => header.show)
    },

    options: {
      get() {
        return {
          page: this.currentPage,
          itemsLength: this.totalItems,
          itemsPerPage: this.itemsPerPage,
        }
      },

      set(event) {
        this.itemsPerPage = event.itemsPerPage
        this.currentPage = event.page
        this.$emit('updateTable', {
          itemsPerPage: this.itemsPerPage,
          page: this.currentPage,
        })
      },
    },
  },

  methods: {
    ...mapActions([
      'setAuditCurrentPage',
      'clearAuditFilters',
      'setAuditFilters',
    ]),

    ...mapMutations({
      setAuditLimitItems: 'SET_LIMIT',
    }),

    formatDate(date, format) {
      return utils.dates.formatDate(date, format)
    },

    formatEventDescription(log) {
      return log.eventDescription.replace(
        '@@sourceAccountName@@',
        log.sourceAccountName
      )
    },

    formatTargetIdLink(log) {
      if (log.targetType === 'rev') {
        return `/rev/${log.targetOrganizationId}/${log.targetId}`
      }

      if (log.targetType === 'challenge') {
        return `/challenge/${log.targetOrganizationId}/${log.targetId}`
      }

      return log.targetId
    },

    showRow(id) {
      if (this.rowShown === id) {
        this.rowShown = null
      } else {
        this.rowShown = id
      }
    },

    closeModal(e) {
      e.preventDefault()
      this.modalIsOpen = false
      this.context = {}
    },

    openContextModal(log) {
      this.modalIsOpen = true
      this.context = log.currentVersion || log.context
    },

    onToggleColumn({ index, value }) {
      this.defaultHeaders[this.headers[index].value].show = value
    },

    handleChangeCurrentPage(event) {
      this.currentPage = event
      return this.emitUpdateTableEvent()
    },

    handleChangeItemsPerPage(event) {
      this.itemsPerPage = event
      return this.emitUpdateTableEvent()
    },

    emitUpdateTableEvent() {
      this.setAuditCurrentPage(this.currentPage)
      this.setAuditLimitItems({ limit: this.itemsPerPage })
      this.$emit('updateTable')
    },
    onFiltersSet(filters) {
      const keys = Object.keys(filters)
      keys.forEach(key => {
        if (filters[key] === 'any') {
          delete filters[key]
        }
      })

      this.setAuditFilters(filters)
      return this.emitUpdateTableEvent()
    },

    onClearFilters() {
      this.clearAuditFilters()
      this.setAuditCurrentPage(1)
      return this.emitUpdateTableEvent()
    },
    getEventName(event) {
      let eventFound = this.eventOptions.find(e => e.value === event)
      return eventFound?.text ?? event
    },
    getTargetTypeName(targetType) {
      let targetTypeFound = this.targetTypeOptions.find(
        t => t.value === _.camelCase(targetType)
      )
      return targetTypeFound?.text ?? targetType
    },
  },
}
</script>

<style lang="scss" scoped>
.filter-sticky {
  position: sticky;
  top: 70px;
}
</style>
